import { Auth } from 'aws-amplify'

import { amplifyConfiguration } from './amplifyConfiguration'
import { getEmail } from '@util/thrive-user-data'
import { CognitoUser } from '.'

/**
 * a getter for procuring the user authentication status.
 *
 * Amplify's `currentAuthenticatedUser` uses its own cached auth value — if it's
 * stale it will perform a network request to procure the latest auth state and
 * reset its cached value.
 *
 * when a user is unauthenticated, the method will throw an error. we don't want this.
 * instead we return null and inform the associated React Context to kick the user out
 * of the web app.
 *
 * https://aws-amplify.github.io/docs/js/authentication#retrieve-current-authenticated-user
 */
export async function getCurrentAuthenticatedUser(): Promise<CognitoUser | null> {
  // protect against attempting to get the current user before Amplify is configured
  if (!amplifyConfiguration.isConfigured) {
    console.warn('attempting to get auth status without Amplify configuration.')

    // attempt to configure Amplify based on the cached email
    const cachedEmail = getEmail()
    if (cachedEmail) {
      await amplifyConfiguration.configureViaEmail(cachedEmail)
    } else {
      return null
    }
  }

  if (window.Cypress) {
    const AuthMocked = await import(
      '@util/amplify-mock-implementations/mocked-cognito-api'
    )
    return AuthMocked.default.getCurrentAuthenticatedUser()
  }

  // with Amplify configured, get the user
  try {
    const user = await Auth.currentAuthenticatedUser()
    return user
  } catch (e) {
    return null
  }
}
